import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './FanWallPage.css';
import {
  getApprovedFanPosts,
  getFanPostImages,
  getFanImagePublicUrl,
  createFanPost,
  addFanPostImage,
  uploadFanImage,
  type FanPost,
} from '../services/api/fanwall';

const MAX_MESSAGE_LENGTH = 1000;
const COOLDOWN_MS = 60000;

export default function FanWallPage() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<FanPost[]>([]);
  const [imagesByPost, setImagesByPost] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [author, setAuthor] = useState('');
  const [message, setMessage] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastSubmit, setLastSubmit] = useState<number>(0);

  function load() {
    getApprovedFanPosts()
      .then(async (list) => {
        setPosts(list);
        const map: Record<string, string[]> = {};
        await Promise.all(
          list.map(async (p) => {
            const imgs = await getFanPostImages(p.id);
            map[p.id] = imgs.map((i) => getFanImagePublicUrl(i.storage_path));
          })
        );
        setImagesByPost(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    if (Date.now() - lastSubmit < COOLDOWN_MS) {
      setSubmitError('Please wait a minute before posting again.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const postId = await createFanPost({ author_name: author.trim() || undefined, message: message.trim().slice(0, MAX_MESSAGE_LENGTH) });
      if (imageFile && (imageFile.type.startsWith('image/')) && imageFile.size <= 5 * 1024 * 1024) {
        const path = await uploadFanImage(postId, imageFile);
        await addFanPostImage(postId, path, 0);
      }
      setMessage('');
      setAuthor('');
      setImageFile(null);
      setLastSubmit(Date.now());
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to post');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="page-loading">{t('common.loading')}</div>;

  return (
    <div className="fanwall-page page">
      <h1 className="fanwall-page__title">{t('nav.fanwall')}</h1>

      <form onSubmit={handleSubmit} className="fanwall-form">
        <p className="fanwall-form__note">Posts are moderated before they appear.</p>
        <input
          type="text"
          placeholder="Your name (optional)"
          value={author}
          onChange={(e) => setAuthor(e.target.value.slice(0, 100))}
          className="fanwall-form__input"
        />
        <textarea
          placeholder="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
          rows={4}
          required
          className="fanwall-form__textarea"
        />
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
          className="fanwall-form__file"
        />
        <button type="submit" disabled={submitting} className="fanwall-form__submit">
          {submitting ? 'Posting...' : 'Post'}
        </button>
        {submitError && <p className="fanwall-form__error" role="alert">{submitError}</p>}
      </form>

      <div className="fanwall-list">
        {posts.map((p) => (
          <article key={p.id} className="fanwall-post">
            {p.author_name && <p className="fanwall-post__author">{p.author_name}</p>}
            <p className="fanwall-post__message">{p.message}</p>
            {imagesByPost[p.id]?.length > 0 && (
              <div className="fanwall-post__images">
                {imagesByPost[p.id].map((url, i) => (
                  <img key={i} src={url} alt="" />
                ))}
              </div>
            )}
          </article>
        ))}
        {posts.length === 0 && <p className="page-empty">No posts yet.</p>}
      </div>
    </div>
  );
}
